import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AxiosProgressEvent } from 'axios';
import { v4 as uuid } from 'uuid';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getProfile } from 'app-components/flux/profile/profileSelectors';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { getLogger, httpClientInterface, tracker } from 'app-components/utils/core';
import { getAxios, getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  appendReasoningDelta,
  appendResponse,
  appendStreamingDelta,
  clearReasoningMessage,
  clearStreamingMessage,
  closeChat,
  setConversationError,
  setConversationLoading,
  setReasoningMessage,
  setThreadTitle,
} from './aiChatActions';
import { attachChartsToHistoricalMessages } from './aiChatChartUtils';
import { parseConversationItemsResponse } from './aiChatHistory';
import * as aiChatSelectors from './aiChatSelectors';
import type {
  AiChatHiddenContext,
  ChartRequest,
  ChatAgentValue,
  ConversationItemsResponse,
  ConversationPageResponse,
  CreateConversationResponse,
} from './aiChatTypes';
import { ChatAgents, DEFAULT_AGENT } from './aiChatTypes';
import { buildClientTools, getChartTransformer, getToolHandler } from './tools/llmToolsCatalog';
import type { ToolDefinition, ToolHandler, ToolMessage, ToolResponse } from './tools/llmToolsTypes';

const log = getLogger('LLM Thunks');

type ConversationContextPayload = {
  text: string;
  data: Record<string, unknown>;
};

const postConversationContext = async (conversationId: string, payload: ConversationContextPayload): Promise<void> => {
  const contextUrl = `${getServiceUrl()}/llm/v1/conversations/${conversationId}/items/context`;
  const contextPayload = {
    text: payload.text,
    role: 'SYSTEM',
    data: payload.data,
  };

  await httpClientInterface.send('POST', contextUrl, contextPayload);
};

type CreateConversationOptions = {
  agent?: ChatAgentValue;
  hiddenContext?: AiChatHiddenContext;
};

export const createAIConversation = createAsyncThunk<
  CreateConversationResponse,
  CreateConversationOptions | undefined,
  {
    rejectValue: string;
  }
>('llm/createAIConversation', async (options, thunkApi) => {
  const { agent, hiddenContext } = options ?? {};
  const url = `${getServiceUrl()}/llm/v1/conversations`;
  log.debug('Creating AI insights conversation', { url });

  try {
    const state = thunkApi.getState() as RootState;
    const effectiveAgent = agent ?? aiChatSelectors.getDefaultChatAgent(state);

    const postCreate = async (requestedAgent: ChatAgentValue) => {
      const requestPayload = { agent: requestedAgent };
      log.debug('createAIConversation: Full request payload before POST', {
        url,
        payload: JSON.stringify(requestPayload, null, 2),
      });
      return (await httpClientInterface.send('POST', url, requestPayload)) as { data: CreateConversationResponse };
    };

    // Only auto-selected agents fall back — an explicitly requested agent is honored as-is.
    const canFallBack = !agent && effectiveAgent !== DEFAULT_AGENT;

    let usedAgent = effectiveAgent;
    let res: { data: CreateConversationResponse };
    try {
      res = await postCreate(effectiveAgent);
    } catch (createError: any) {
      const status = createError?.response?.status;
      // A 4xx means the backend rejected this agent (e.g. not provisioned yet). 5xx and
      // network failures are transient and must not silently downgrade the agent.
      const agentUnavailable = typeof status === 'number' && status >= 400 && status < 500;

      if (!canFallBack || !agentUnavailable) {
        throw createError;
      }

      log.warn('createAIConversation: Agent unavailable, falling back to default agent', {
        requestedAgent: effectiveAgent,
        fallbackAgent: DEFAULT_AGENT,
        status,
      });
      usedAgent = DEFAULT_AGENT;
      res = await postCreate(DEFAULT_AGENT);
    }

    log.debug('createAIConversation: Conversation created successfully', {
      conversationId: res?.data?.id,
      agent: usedAgent,
    });

    const id = res?.data?.id;

    if (!id) {
      return thunkApi.rejectWithValue('Conversation response is missing an id');
    }

    const profile = getProfile(state);
    const subscription = subscriptionsSelectors.getActiveSubscription(state);

    log.debug('createAIConversation: Sending user context', { conversationId: id });

    // A new chat gets one context item containing both the profile and the launching feature's data.
    await postConversationContext(id, {
      text: ['User profile and subscription data', hiddenContext?.text].filter(Boolean).join('\n\n'),
      data: {
        firstName: profile?.firstName,
        companyName: profile?.companyName,
        primaryAddress: profile?.primaryAddress,
        subscription: subscription
          ? {
              tierName: subscription.tierName,
              tier: subscription.tierUriName,
              isActive: subscription.active,
              isInTrialPeriod: subscription.isInTrialPeriod,
              renewalFrequency: subscription.renewalFrequency === 'P1M' ? 'monthly' : 'annual',
              expireOn: subscription.expireOn,
            }
          : null,
        ...(hiddenContext && { messageContext: hiddenContext.data ?? {} }),
      },
    }).catch((err) => {
      log.warn('createAIConversation: Failed to send user context', { error: err });
      if (hiddenContext) throw err;
    });

    return { id: res.data.id, agent: usedAgent };
  } catch (error: any) {
    const errorDetail = error?.response?.data?.errors?.[0]?.detail;
    const errorTitle = error?.response?.data?.errors?.[0]?.title;
    const message =
      errorDetail || errorTitle || (error instanceof Error ? error.message : 'Failed to create AI conversation');
    log.error('Failed to create AI conversation', {
      error: message,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      responseData: error?.response?.data,
      requestData: error?.config?.data,
    });
    return thunkApi.rejectWithValue(message);
  }
});

type ConversationPageResult = ConversationPageResponse & { requestGeneration: number };
type ConversationRequestError = { message: string; requestGeneration: number };
type DeleteAllConversationsError = { message: string; deletedAgents: string[] };

export const fetchInitialConversations = createAsyncThunk<
  ConversationPageResult,
  void,
  { rejectValue: ConversationRequestError }
>(
  'llm/fetchInitialConversations',
  async (_, { getState, rejectWithValue }) => {
    const requestGeneration = (getState() as RootState).aiChatStore.conversationsRequestGeneration;
    const params = new URLSearchParams();
    params.set('page', '0');
    params.set('size', '20');

    const url = `${getServiceUrl()}/llm/v1/conversations?${params.toString()}`;
    log.debug('Fetching initial AI insights conversations', { url });

    try {
      const res = (await httpClientInterface.send('GET', url)) as {
        data: ConversationPageResponse;
      };
      log.debug('fetchInitialConversations: Response received', { response: res.data });
      return { ...res.data, requestGeneration };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to get AI conversations';
      log.error('Failed to get AI conversations', error);
      return rejectWithValue({ message, requestGeneration });
    }
  },
  {
    condition: (_, { getState }) => {
      const { conversationsMeta, isFetchingInitialConversations } = (getState() as RootState).aiChatStore;

      if (conversationsMeta.page >= 0) {
        return false;
      }

      return !isFetchingInitialConversations;
    },
  },
);

export const loadNextConversationsPage = createAsyncThunk<
  ConversationPageResult,
  void,
  { rejectValue: ConversationRequestError }
>('llm/loadNextConversationsPage', async (_, { getState, rejectWithValue }) => {
  const state = getState() as RootState;
  const {
    conversationsRequestGeneration: requestGeneration,
    conversationsMeta: { page, pageSize, hasMore },
  } = state.aiChatStore;

  // nothing more to load, bail early
  if (!hasMore && page >= 0) {
    return rejectWithValue({ message: 'No more pages', requestGeneration });
  }

  // page === -1 => nothing loaded yet -> start at 0
  const nextPage = page < 0 ? 0 : page + 1;

  const params = new URLSearchParams();
  params.set('page', String(nextPage));
  params.set('size', String(pageSize));

  const url = `${getServiceUrl()}/llm/v1/conversations?${params.toString()}`;
  log.debug('Fetching AI insights conversations page', {
    url,
    nextPage,
    pageSize,
  });

  try {
    const res = (await httpClientInterface.send('GET', url)) as {
      data: ConversationPageResponse;
    };
    return { ...res.data, requestGeneration };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to get AI conversations';
    log.error('Failed to get AI conversations', error);
    return rejectWithValue({ message, requestGeneration });
  }
});

export const fetchConversationMessagesById = createAsyncThunk<
  ConversationItemsResponse,
  string,
  {
    rejectValue: string;
  }
>('llm/fetchConversationMessagesById', async (conversationId, { rejectWithValue }) => {
  const params = new URLSearchParams();
  params.set('limit', '10');
  const url = `${getServiceUrl()}/llm/v1/conversations/${conversationId}/items?${params.toString()}`;
  log.debug('Fetching conversation items', { url, conversationId });

  try {
    const res = await httpClientInterface.send('GET', url);
    const page = parseConversationItemsResponse(res.data);
    return { ...page, items: attachChartsToHistoricalMessages(page.items) };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to fetch conversation';
    log.error('Failed to fetch conversation', error);
    return rejectWithValue(message);
  }
});

export const loadMoreMessages = createAsyncThunk<
  ConversationItemsResponse & { conversationId: string },
  string,
  { rejectValue: string }
>('llm/loadMoreMessages', async (conversationId, { getState, rejectWithValue }) => {
  if (!conversationId) {
    return rejectWithValue('No conversation ID');
  }

  const state = getState() as RootState;
  const conv = state.aiChatStore.conversationsData[conversationId];

  const nextPageCursor = conv?.nextPageCursor ?? null;
  const hasMoreItems = conv?.hasMoreItems ?? false;

  if (!hasMoreItems || !nextPageCursor) {
    return { items: [], nextPage: null, conversationId };
  }

  const params = new URLSearchParams();
  params.set('limit', '10');
  params.set('page', nextPageCursor);

  const url = `${getServiceUrl()}/llm/v1/conversations/${conversationId}/items?${params.toString()}`;
  log.debug('Fetching next page of conversation items', {
    url,
    conversationId,
    cursor: nextPageCursor,
  });

  try {
    const res = await httpClientInterface.send('GET', url);
    return { ...parseConversationItemsResponse(res.data), conversationId };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to fetch more items';
    log.error('Failed to fetch more conversation items', error);
    return rejectWithValue(message);
  }
});

export const deleteAIConversation = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string;
  }
>('llm/deleteAIConversation', async (conversationId, { rejectWithValue }) => {
  const url = `${getServiceUrl()}/llm/v1/conversations/${conversationId}`;
  log.debug('Deleting AI conversation', { url, conversationId });

  try {
    await httpClientInterface.send('DELETE', url);
    log.debug('deleteAIConversation: Conversation deleted successfully', {
      conversationId,
    });
    return conversationId;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete conversation';
    log.error('Failed to delete conversation', { conversationId, error });
    return rejectWithValue(message);
  }
});

export const deleteAllAIConversations = createAsyncThunk<string[], void, { rejectValue: DeleteAllConversationsError }>(
  'llm/deleteAllAIConversations',
  async (_, { getState, rejectWithValue }) => {
    const loadedAgents = (getState() as RootState).aiChatStore.conversationsList.map(({ agent }) => agent);
    const agents = [...new Set([...Object.values(ChatAgents), ...loadedAgents])];
    const deletedAgents: string[] = [];

    for (const agent of agents) {
      try {
        const params = new URLSearchParams({ agent });
        const url = `${getServiceUrl()}/llm/v1/conversations?${params.toString()}`;
        log.debug('Deleting all AI conversations for agent', { url, agent });
        await httpClientInterface.send('DELETE', url);
        deletedAgents.push(agent);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to delete all conversations';
        log.error('Failed to delete all conversations', { agent, error, deletedAgents });
        return rejectWithValue({ message, deletedAgents });
      }
    }

    log.debug('deleteAllAIConversations: All conversations deleted successfully', { agents });
    return agents;
  },
);

export const closeAIConversation = createAsyncThunk<void, void>(
  'llm/closeAIConversation',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const conversationId = state.aiChatStore.currentConversationId;
    const shouldDeleteConversation = conversationId
      ? aiChatSelectors.selectConversationIsEmpty(state, conversationId)
      : false;

    dispatch(closeChat());

    if (conversationId && shouldDeleteConversation) {
      await dispatch(deleteAIConversation(conversationId)).unwrap();
    }
  },
);

const parseSSEEvents = (buffer: string): { events: string[]; remaining: string } => {
  const chunks = buffer.split('\n\n');
  const remaining = chunks.pop() ?? '';
  const events: string[] = [];

  for (const chunk of chunks) {
    for (const line of chunk.split('\n')) {
      if (line.startsWith('data:')) {
        const data = line.slice(5).trim();
        if (data) events.push(data);
      }
    }
  }

  return { events, remaining };
};

type SendLlmMessageOptions = {
  conversationId: string;
  text: string;
  /**
   * Model-only context posted as a hidden SYSTEM item immediately before the user
   * message. Persisted in the thread for model input but filtered from the UI.
   */
  hiddenContext?: AiChatHiddenContext;
  tools?: ToolDefinition[];
  toolHandlers?: Record<string, ToolHandler>;
  agent?: ChatAgentValue;
  trackSend?: boolean;
  enableWebSearch?: boolean;
};

type PendingToolCall = {
  callId: string;
  name: string;
  arguments: string;
};

const getReasoningStreamItemId = (msg: Record<string, unknown>): string | null => {
  const itemId = msg.itemId ?? msg.item_id;
  return typeof itemId === 'string' ? itemId : null;
};

const activeAbortControllers = new Map<string, AbortController>();

export const sendLlmMessage = createAsyncThunk<void, SendLlmMessageOptions, { rejectValue: string }>(
  'llm/sendMessage',
  async (options, thunkApi) => {
    const {
      conversationId,
      text,
      hiddenContext,
      tools: customTools,
      toolHandlers: customToolHandlers,
      agent,
      trackSend,
      enableWebSearch = true,
    } = options;
    const { dispatch, getState } = thunkApi;

    const axios = getAxios();
    if (!axios) {
      return thunkApi.rejectWithValue('HTTP client not initialized');
    }

    const existingConversation = (getState() as RootState).aiChatStore.conversationsData[conversationId];
    if (!existingConversation) {
      log.error('sendLlmMessage: Conversation state is not initialized', { conversationId });
      return thunkApi.rejectWithValue('Conversation is not initialized');
    }

    if (trackSend) {
      const sendIndex =
        existingConversation.messages.filter((item) => item.role === 'user' && item.type === 'message').length + 1;

      tracker.track(tracker.events.AIChatSend, {
        conversationId,
        sendIndex,
      });
    }

    // Cancel any existing request for this conversation
    activeAbortControllers.get(conversationId)?.abort();

    if (hiddenContext) {
      try {
        await postConversationContext(conversationId, {
          text: hiddenContext.text,
          data: hiddenContext.data ?? {},
        });
      } catch (error) {
        log.warn('sendLlmMessage: Failed to post hidden context', { conversationId, error });
      }
    }

    // Add user message to state
    dispatch(
      appendResponse({
        conversationId,
        item: {
          role: 'user',
          type: 'message',
          content: { input_type: 'TEXT', text },
          id: uuid(),
          createdAt: new Date().toISOString(),
        },
      }),
    );

    dispatch(clearStreamingMessage(conversationId));
    dispatch(clearReasoningMessage(conversationId));
    dispatch(setConversationLoading({ conversationId, loading: true }));
    dispatch(setConversationError({ conversationId, error: null }));

    const isChartsEnabled = !!featureFlagsSelectors.getFeatureFlag(getState() as RootState, 'aiCharts');

    // When customTools are provided, use only those — caller is explicit about what they need.
    // Otherwise fall back to core + platform tools for the normal chat flow.
    let allTools: ToolDefinition[];
    if (customTools) {
      allTools = customTools;
    } else {
      const coreTools = (await buildClientTools()) ?? [];
      const platformTools = (getState() as RootState).aiChatStore.platformSpecificTools ?? [];
      allTools = [...coreTools, ...platformTools];
      if (!isChartsEnabled) {
        allTools = allTools.filter((t) => t.name !== 'render_chart');
      }
    }

    // Determine agent
    const effectiveAgent = agent ?? existingConversation.agent ?? DEFAULT_AGENT;
    const enableKnowledgeBaseRetrieval = !!featureFlagsSelectors.getFeatureFlag(
      getState() as RootState,
      'aiKnowledgeBase',
    );

    const pendingToolCalls: PendingToolCall[] = [];
    const collectedCharts: ChartRequest[] = [];
    let reasoningStreamActive = false;
    let activeReasoningStreamItemId: string | null = null;

    const processEvent = (raw: string) => {
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(raw);
      } catch {
        log.error('sendLlmMessage: Invalid JSON message');
        return { isComplete: false };
      }

      log.debug('sendLlmMessage: Processing event', { type: msg.type });

      switch (msg.type) {
        case 'response.function_call_arguments.done': {
          const toolCall = msg as Partial<ToolMessage>;
          if (!toolCall.callId) {
            log.warn('sendLlmMessage: Tool call missing callId', { name: toolCall.name });
            break;
          }

          if (!toolCall.name || !toolCall.arguments) {
            log.warn('sendLlmMessage: Tool call missing name or arguments', {
              callId: toolCall.callId,
            });
            break;
          }

          dispatch(
            appendResponse({
              conversationId,
              item: {
                role: 'assistant',
                type: 'function_call',
                content: {
                  name: toolCall.name,
                  arguments: toolCall.arguments,
                  callId: toolCall.callId,
                },
                id: uuid(),
                createdAt: new Date().toISOString(),
              },
            }),
          );

          pendingToolCalls.push({
            callId: toolCall.callId,
            name: toolCall.name,
            arguments: toolCall.arguments,
          });
          break;
        }

        case 'server.stream.completed': {
          return { isComplete: true };
        }

        case 'response.reasoning_summary_text.done':
          if (msg.text) {
            dispatch(setReasoningMessage({ conversationId, message: msg.text as string }));
          }
          reasoningStreamActive = false;
          activeReasoningStreamItemId = null;
          break;

        case 'response.reasoning_summary_text.delta': {
          const itemId = getReasoningStreamItemId(msg);
          const isNewReasoningSegment =
            !reasoningStreamActive ||
            (!!itemId && !!activeReasoningStreamItemId && itemId !== activeReasoningStreamItemId);

          if (isNewReasoningSegment) {
            dispatch(clearReasoningMessage(conversationId));
          }

          if (msg.delta) {
            const fixed = (msg.delta as string).replace(/\\n/g, '\n');
            dispatch(appendReasoningDelta({ conversationId, delta: fixed }));
          }
          reasoningStreamActive = true;
          activeReasoningStreamItemId = itemId ?? activeReasoningStreamItemId;
          break;
        }

        case 'response.output_text.delta':
        case 'response.audio_transcript.delta':
          dispatch(clearReasoningMessage(conversationId));
          reasoningStreamActive = false;
          activeReasoningStreamItemId = null;
          if (msg.delta) {
            const fixed = (msg.delta as string).replace(/\\n/g, '\n');
            dispatch(appendStreamingDelta({ conversationId, delta: fixed }));
          }
          break;

        case 'response.output_text.done':
        case 'response.audio_transcript.done': {
          const metadata = msg.metadata as { title?: string } | undefined;
          if (metadata?.title) {
            dispatch(setThreadTitle({ conversationId, title: metadata.title }));
          }
          const responseText = (msg.text as string) ?? '';
          dispatch(
            appendResponse({
              conversationId,
              item: {
                role: 'assistant',
                type: 'message',
                content: { input_type: 'TEXT', text: responseText },
                id: uuid(),
                createdAt: new Date().toISOString(),
              },
              charts: collectedCharts.length > 0 ? collectedCharts : undefined,
            }),
          );
          for (const chart of collectedCharts) {
            tracker.track(tracker.events.AIChartCreate, { chartType: chart.chartType });
          }
          dispatch(setConversationLoading({ conversationId, loading: false }));
          dispatch(clearReasoningMessage(conversationId));
          reasoningStreamActive = false;
          activeReasoningStreamItemId = null;
          dispatch(clearStreamingMessage(conversationId));
          break;
        }

        default:
          break;
      }

      return { isComplete: false };
    };

    const executeToolCalls = async (): Promise<ToolResponse[]> => {
      const platformHandlers = (getState() as RootState).aiChatStore.platformSpecificToolsHandlers ?? {};
      const outputs: ToolResponse[] = [];

      for (const toolCall of pendingToolCalls) {
        const handler =
          customToolHandlers?.[toolCall.name] ?? platformHandlers[toolCall.name] ?? getToolHandler(toolCall.name);

        if (!handler) {
          log.warn(`sendLlmMessage: No handler for tool "${toolCall.name}"`);
          continue;
        }

        try {
          const result = await Promise.resolve(
            handler({
              callId: toolCall.callId,
              name: toolCall.name,
              arguments: toolCall.arguments,
            }),
          );

          if (result) {
            outputs.push(result);
          }
        } catch (err) {
          log.error(`sendLlmMessage: Tool "${toolCall.name}" failed`, { err });
        }
      }

      return outputs;
    };

    const postSSE = async (payload: unknown): Promise<boolean> => {
      let buffer = '';
      let processed = 0;
      let streamCompleted = false;

      const requestController = new AbortController();
      activeAbortControllers.set(conversationId, requestController);

      const handleProgress = (e: AxiosProgressEvent) => {
        if (streamCompleted) return;

        const responseText = (e.event?.target as XMLHttpRequest)?.responseText ?? '';
        if (responseText.length <= processed) return;

        buffer += responseText.substring(processed);
        processed = responseText.length;

        const { events, remaining } = parseSSEEvents(buffer);
        buffer = remaining;

        for (const event of events) {
          const { isComplete } = processEvent(event);
          if (isComplete) {
            streamCompleted = true;
            requestController.abort();
          }
        }
      };

      try {
        await axios.post(`${getServiceUrl()}/llm/v1/conversations/${conversationId}/items`, payload, {
          headers: { Accept: 'text/event-stream' },
          signal: requestController.signal,
          onDownloadProgress: handleProgress,
        });
      } catch (error) {
        if (requestController.signal.aborted && streamCompleted) {
          // Expected: we abort the connection after the server signals stream complete
        } else if (requestController.signal.aborted) {
          throw new Error('Cancelled');
        } else {
          log.error('sendLlmMessage: Request failed', { error });
          throw error;
        }
      }

      return streamCompleted;
    };

    try {
      // Initial message request
      const initialPayload = {
        agent: effectiveAgent,
        options: { enableParallelToolCalls: true, enableWebSearch, enableKnowledgeBaseRetrieval },
        userPrompt: { text },
        tools: allTools,
      };

      await postSSE(initialPayload);

      // Tool call loop
      while (pendingToolCalls.length > 0) {
        const toolOutputs = await executeToolCalls();
        pendingToolCalls.length = 0;

        if (isChartsEnabled) {
          // Reset each round — only charts from the current tool-output batch are attached
          // to the assistant message. An earlier round's render_chart result is discarded
          // once the LLM continues; it was already attached to that round's message.
          collectedCharts.length = 0;
          for (const toolOutput of toolOutputs) {
            const transformer = getChartTransformer(toolOutput.name);
            if (transformer) {
              const result = transformer(toolOutput.output);
              if (result) {
                // Keep the chart with the most data points so a 12-month YTD trend beats
                // a single-month breakdown regardless of call order. Ties go to the newer
                // chart so re-render requests take effect.
                const incoming = Array.isArray(result) ? result : [result];
                const incomingSize = Math.max(...incoming.map((c) => c.data?.length ?? 0));
                const currentSize = Math.max(...collectedCharts.map((c) => c.data?.length ?? 0), 0);
                if (incomingSize >= currentSize) {
                  collectedCharts.length = 0;
                  collectedCharts.push(...incoming);
                }
              }
            }
          }
        }

        if (toolOutputs.length === 0) {
          dispatch(setConversationLoading({ conversationId, loading: false }));
          break;
        }

        dispatch(setConversationLoading({ conversationId, loading: true }));

        const toolPayload = {
          agent: effectiveAgent,
          options: { enableParallelToolCalls: true, enableWebSearch, enableKnowledgeBaseRetrieval },
          functionCallOutputs: toolOutputs,
          tools: allTools,
        };

        await postSSE(toolPayload);
      }
    } catch (error) {
      if ((error as Error).message === 'Cancelled') {
        dispatch(setConversationLoading({ conversationId, loading: false }));
        return;
      }

      dispatch(setConversationLoading({ conversationId, loading: false }));
      dispatch(setConversationError({ conversationId, error: 'Something went wrong, please try again later' }));
      dispatch(
        appendResponse({
          conversationId,
          item: {
            role: 'assistant',
            type: 'error',
            content: { input_type: 'TEXT', text: 'Something went wrong, please try again later' },
            id: uuid(),
            createdAt: new Date().toISOString(),
          },
        }),
      );
    } finally {
      activeAbortControllers.delete(conversationId);
    }

    return undefined;
  },
);
